/**
 * Type-preserving edit helpers for the Step-1 discharge summary form.
 *
 * The form renders every value into a single-line TextField. Before this module
 * an array was joined with ", " and an object was JSON.stringify'd, and whatever
 * the doctor typed was written straight back — so touching an array-valued field
 * silently replaced the array with a string, and touching an object-valued one
 * replaced it with its JSON text. Two fields worked around it by hand
 * (past_medical_history, past_surgical_history); every other one did not.
 */

const isPlainObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);

/** How a value may be edited in a single-line field. */
export const fieldKind = (value) => {
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "number") return "number";
  if (Array.isArray(value)) {
    // An array of primitives round-trips through comma-separated text. An array
    // of objects (medications, for example) does not — those get their own
    // per-field editors in the form.
    return value.every((v) => v === null || v === undefined || typeof v !== "object")
      ? "csv"
      : "locked";
  }
  if (isPlainObject(value)) return "locked";
  return "text";
};

/** Render a value for a text input. */
export const toInputValue = (value, kind) => {
  if (value === null || value === undefined) return "";
  if (kind === "csv") {
    return value.filter((v) => v !== null && v !== undefined && v !== "").join(", ");
  }
  if (kind === "locked") {
    try {
      return JSON.stringify(value);
    } catch {
      return "";
    }
  }
  return String(value);
};

/** Convert edited text back to the field's original shape. */
export const fromInputValue = (raw, kind) => {
  if (kind === "csv") {
    return String(raw)
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
  }
  if (kind === "number") {
    if (String(raw).trim() === "") return "";
    const n = Number(raw);
    // Non-numeric input is kept verbatim rather than becoming NaN. "two weeks"
    // in a follow-up interval is wrong, but silently blanking what the doctor
    // typed is worse.
    return Number.isNaN(n) ? raw : n;
  }
  return raw;
};
